import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { Provider as GraphQLProvider } from 'urql';

import { useGeolocationIntegration } from '../../../geolocation/util/geolocation-context';
import { usePrevious } from '../../../shared/hooks/use-previous';
import { useSignIn } from '../../../shared/hooks/use-sign-in';
import type { FCWithChildren } from '../../../shared/utils/component.interface';
import { getClient } from '../../graphql';
import { useNetworkToast } from '../../hooks/use-network-toast';
import { getUser } from '../../util/auth';

export const GraphProvider: FCWithChildren<unknown> = ({ children }) => {
    const auth = useAuth();
    const isAuthenticated = auth.isAuthenticated;
    const previousAuthState = usePrevious(isAuthenticated);
    const geoIntegration = useGeolocationIntegration();
    const signIn = useSignIn();
    const networkToastController = useNetworkToast();

    const client = useMemo(() => {
        return getClient(isAuthenticated !== previousAuthState, {
            getToken() {
                const user = getUser();
                return user?.access_token ?? user?.access_token ?? '';
            },
            willAuthError() {
                if (!isAuthenticated) {
                    // Return false as we want non-authenticated users to be able to access some data.
                    return false;
                }

                const user = getUser();
                return user?.expired ?? true;
            },
            getGeoToken() {
                if (geoIntegration) {
                    return geoIntegration.getCurrentToken();
                }

                return;
            },
            logout() {
                console.log('[GraphProvider]', 'Removing user');
                // User got logged out server side. Remove user silently.
                auth.removeUser();
            },
            async loginSilent() {
                console.log('[GraphProvider]', 'Signing in...');
                await auth.signinSilent();
            },
            async login(params) {
                console.log('[GraphProvider]', 'Signing in with acrValues', params);
                await signIn({
                    acrValues: params?.acrValues,
                    returnQueryParameters: params?.returningOperationId
                        ? {
                              operationId: params?.returningOperationId,
                          }
                        : undefined,
                });
            },
            onNetworkError: () => {
                networkToastController.onNetworkError();
            },
            onNetworkSuccess: () => {
                networkToastController.onNetworkSuccess();
            },
        });
    }, [isAuthenticated, previousAuthState, auth, geoIntegration, signIn, networkToastController]);

    return <GraphQLProvider value={client}>{children}</GraphQLProvider>;
};
